import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Protected from "@/components/Protected";
import { useFlags, useConfigLoaded } from "@/lib/flags";
import { homeFor } from "@/config";

import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import AcceptInvite from "@/pages/AcceptInvite";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import SocialCallback from "@/pages/SocialCallback";
import Notifications from "@/pages/Notifications";
import Account from "@/pages/Account";
import LegalPage from "@/pages/LegalPage";
import Pricing from "@/pages/Pricing";

import Overview from "@/pages/owner/Overview";
import Team from "@/pages/owner/Team";
import Projects from "@/pages/owner/Projects";
import Objectives from "@/pages/owner/Objectives";
import ObjectiveDetail from "@/pages/owner/ObjectiveDetail";
import Budgets from "@/pages/owner/Budgets";
import Usage from "@/pages/owner/Usage";
import ActivityPage from "@/pages/owner/Activity";
import Audit from "@/pages/owner/Audit";
import SettingsPage from "@/pages/owner/Settings";
import Memory from "@/pages/owner/Memory";
import Reports from "@/pages/owner/Reports";
import OwnerStore from "@/pages/owner/Store";
import Investigate from "@/pages/owner/Investigate";
import InvestigateSession from "@/pages/owner/InvestigateSession";
import Documents from "@/pages/owner/Documents";
import DocumentPreview from "@/pages/owner/DocumentPreview";
import Scheduler from "@/pages/owner/Scheduler";
import Optout from "@/pages/Optout";

import Sessions from "@/pages/coder/Sessions";
import NewSession from "@/pages/coder/NewSession";
import Workspace from "@/pages/coder/Workspace";
import Timeline from "@/pages/coder/Timeline";
import Messages from "@/pages/Messages";
import InvestorDashboard from "@/pages/investor/Dashboard";

import Companies from "@/pages/admin/Companies";
import Runners from "@/pages/admin/Runners";
import Models from "@/pages/admin/Models";
import AdminSessions from "@/pages/admin/Sessions";
import GitHubApp, { GitHubAppCreated } from "@/pages/admin/GitHubApp";
import AdminStore from "@/pages/admin/Store";
import GitHubConnect from "@/pages/GitHubConnect";
import ConnectAuthorize from "@/pages/ConnectAuthorize";

function Root() {
  const { user, ready } = useAuth();
  if (!ready) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={homeFor(user.role)} replace />;
}

// The founder's pages were at /owner and the team's at /coder until 2026-10-17: links in emails already sent, saved
// notifications and Lemon Squeezy checkouts started before then still use them, so they land on the same page at its
// new address.
function Moved({ from, to }) {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={to + pathname.slice(from.length) + search + hash} replace />;
}

export default function App() {
  const flags = useFlags();
  const configLoaded = useConfigLoaded();
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Root />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        <Route path="/accept-invite/:token" element={<AcceptInvite />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/auth/callback/:provider" element={<SocialCallback />} />
        {/* public, no sign-in: the terms, the privacy policy, the refund policy and the prices (FEATURE_STORE) */}
        <Route path="/terms" element={<LegalPage page="terms" />} />
        <Route path="/privacy" element={<LegalPage page="privacy" />} />
        <Route path="/refunds" element={<LegalPage page="refunds" />} />
        <Route path="/pricing" element={<Pricing />} />
        {/* "Stop these scheduled updates" in a founder's investor update: public, no sign-in (backend doc_schedules.py) */}
        <Route path="/optout" element={<Optout />} />
        <Route path="/notifications" element={<Protected roles={["owner", "coder", "viewer", "platform_admin"]}><Notifications /></Protected>} />
        <Route path="/account" element={<Protected roles={["owner", "coder", "viewer", "platform_admin"]}><Account /></Protected>} />

        <Route path="/founder" element={<Protected roles={["owner"]}><Overview /></Protected>} />
        <Route path="/founder/team" element={<Protected roles={["owner"]}><Team /></Protected>} />
        <Route path="/founder/messages" element={<Protected roles={["owner"]}><Messages /></Protected>} />
        <Route path="/founder/messages/:tid" element={<Protected roles={["owner"]}><Messages /></Protected>} />
        <Route path="/founder/projects" element={<Protected roles={["owner"]}><Projects /></Protected>} />
        <Route path="/founder/objectives" element={<Protected roles={["owner"]}><Objectives /></Protected>} />
        <Route path="/founder/objectives/:oid" element={<Protected roles={["owner"]}><ObjectiveDetail /></Protected>} />
        <Route path="/founder/memory" element={<Protected roles={["owner"]}><Memory /></Protected>} />
        <Route path="/founder/budgets" element={<Protected roles={["owner"]}><Budgets /></Protected>} />
        <Route path="/founder/usage" element={<Protected roles={["owner"]}><Usage /></Protected>} />
        <Route path="/founder/activity" element={<Protected roles={["owner"]}><ActivityPage /></Protected>} />
        <Route path="/founder/audit" element={<Protected roles={["owner"]}><Audit /></Protected>} />
        <Route path="/founder/reports" element={<Protected roles={["owner"]}><Reports /></Protected>} />
        <Route path="/founder/settings" element={<Protected roles={["owner"]}><SettingsPage /></Protected>} />
        <Route path="/founder/store" element={<Protected roles={["owner"]}><OwnerStore /></Protected>} />
        {/* Investigate (docs/INVESTIGATE_PLAN.md, FEATURE_INVESTIGATE): no routes at all with the flag off */}
        {flags.investigate && <Route path="/founder/investigate" element={<Protected roles={["owner"]}><Investigate /></Protected>} />}
        {flags.investigate && <Route path="/founder/sessions/:sid/investigate" element={<Protected roles={["owner"]}><InvestigateSession /></Protected>} />}
        {/* the founder's assistant's documents (FEATURE_DOCUMENTS, backend/documents.py) */}
        {flags.documents && <Route path="/founder/documents" element={<Protected roles={["owner"]}><Documents /></Protected>} />}
        {flags.documents && <Route path="/founder/documents/:id" element={<Protected roles={["owner"]}><DocumentPreview /></Protected>} />}
        {flags.documents && <Route path="/founder/documents/schedule" element={<Protected roles={["owner"]}><Scheduler /></Protected>} />}
        {flags.documents && <Route path="/founder/documents/schedule/:sid" element={<Protected roles={["owner"]}><Scheduler /></Protected>} />}
        {/* the Connect GitHub popup: GitHub's install page and authorisation come back to these */}
        <Route path="/github/connect" element={<Protected roles={["owner"]} bare><GitHubConnect /></Protected>} />
        <Route path="/github/setup" element={<Protected roles={["owner"]} bare><GitHubConnect /></Protected>} />
        <Route path="/github/callback" element={<Protected roles={["owner"]} bare><GitHubConnect /></Protected>} />
        {/* the connector's Allow page (FEATURE_CONNECTOR): a founder's AI app sends them here; anyone else is told it's for founders */}
        {flags.connector && <Route path="/connect/authorize" element={<Protected roles={["owner", "coder", "viewer", "platform_admin"]} bare><ConnectAuthorize /></Protected>} />}

        <Route path="/team" element={<Protected roles={["coder"]}><Sessions /></Protected>} />
        <Route path="/team/new" element={<Protected roles={["coder"]}><NewSession /></Protected>} />
        <Route path="/team/messages" element={<Protected roles={["coder"]}><Messages /></Protected>} />
        <Route path="/team/messages/:tid" element={<Protected roles={["coder"]}><Messages /></Protected>} />
        <Route path="/team/session/:id" element={<Protected roles={["coder"]} bare><Workspace /></Protected>} />
        {flags.investigate && <Route path="/team/sessions/:id/timeline" element={<Protected roles={["coder"]}><Timeline /></Protected>} />}

        <Route path="/owner/*" element={<Moved from="/owner" to="/founder" />} />
        <Route path="/coder/*" element={<Moved from="/coder" to="/team" />} />

        {/* investors (FEATURE_INVESTORS): their read-only view and Messages with the founders, nothing else */}
        {flags.investors && <Route path="/investor" element={<Protected roles={["viewer"]}><InvestorDashboard /></Protected>} />}
        {flags.investors && <Route path="/investor/messages" element={<Protected roles={["viewer"]}><Messages /></Protected>} />}
        {flags.investors && <Route path="/investor/messages/:tid" element={<Protected roles={["viewer"]}><Messages /></Protected>} />}

        <Route path="/admin/companies" element={<Protected roles={["platform_admin"]}><Companies /></Protected>} />
        <Route path="/admin/runners" element={<Protected roles={["platform_admin"]}><Runners /></Protected>} />
        <Route path="/admin/models" element={<Protected roles={["platform_admin"]}><Models /></Protected>} />
        <Route path="/admin/sessions" element={<Protected roles={["platform_admin"]}><AdminSessions /></Protected>} />
        <Route path="/admin/github-app" element={<Protected roles={["platform_admin"]}><GitHubApp /></Protected>} />
        <Route path="/admin/github-app/created" element={<Protected roles={["platform_admin"]}><GitHubAppCreated /></Protected>} />
        <Route path="/admin/store" element={<Protected roles={["platform_admin"]}><AdminStore /></Protected>} />

        {/* an address behind a flag isn't sent away before the flags are known */}
        <Route path="*" element={configLoaded ? <Root /> : null} />
      </Routes>
    </BrowserRouter>
  );
}
